"use client";

import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import type { CartItem, Product } from "@/lib/pricing";
import { getWebsitePrice } from "@/lib/pricing";
import type { StockInfo } from "@/hooks/useCart";
import type { ProductFeasibility } from "@/hooks/useStockEngine";

interface CartSidebarProps {
  cart: Record<string, CartItem>;
  products: Product[];
  channelLevelUps: any[];
  stockAvailability: Record<string, StockInfo>;
  feasibility: Record<string, ProductFeasibility>;
  subtotal: number;
  referralDiscount: number;
  total: number;
  referralBalance: number;
  useReferralBalance: boolean;
  setUseReferralBalance: (v: boolean) => void;
  isAuthenticated: boolean;
  hasMobile: boolean | null;
  validationResult: any;
  isSubmittingOrder: boolean;
  hasOutOfStockItems: boolean;
  outOfStockNames: string[];
  onClear: () => void;
  onCheckout: () => void;
  onUpdateQty?: (key: string, qty: number) => void;
  onRemove?: (key: string, qty?: number) => void;
  isMobile?: boolean;
  paymentConfig?: any;
  charges?: {
    processingChargeEnabled: boolean;
    processingChargePercent: number;
  };
  activeOffers?: Record<string, any>;
}

const geist = "'Geist Mono', monospace";

export default function CartSidebar({
  cart,
  products,
  channelLevelUps,
  stockAvailability,
  feasibility,
  subtotal,
  referralDiscount,
  total,
  referralBalance,
  useReferralBalance,
  setUseReferralBalance,
  isAuthenticated,
  hasMobile,
  validationResult,
  isSubmittingOrder,
  hasOutOfStockItems,
  outOfStockNames,
  onClear,
  onCheckout,
  onUpdateQty,
  onRemove,
  isMobile = false,
  paymentConfig,
  charges,
  activeOffers,
}: CartSidebarProps) {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const cartEntries = Object.entries(cart);
  const isEmpty = cartEntries.length === 0;
  const isCheckoutDisabled =
    isEmpty ||
    !validationResult ||
    isSubmittingOrder ||
    hasOutOfStockItems ||
    (isAuthenticated && !agreedToTerms);

  return (
    <div
      className="flex flex-col h-full px-3 pt-2 pb-3"
      style={{ fontFamily: geist }}
    >
      {/* Cart Items */}
      <div
        className={`flex-1 overflow-y-auto flex flex-col gap-2.5 ${isEmpty ? "justify-center" : ""}`}
      >
        {isEmpty ? (
          <div className="text-center py-8">
            <svg
              className="w-14 h-14 mx-auto mb-3 text-gray-200"
              fill="none"
              viewBox="0 0 22 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <rect x="0.5" y="6.5" width="26" height="17" rx="0.5" />
              <path
                d="M5.5 9V5.5C5.5 2.46 7.96 0 11 0s5.5 2.46 5.5 5.5V9"
                strokeLinecap="round"
              />
            </svg>
            <p className="text-xs font-medium text-slate-600 uppercase tracking-wider">
              Your cart is empty
            </p>
          </div>
        ) : (
          cartEntries.map(([id, item], index) => {
            let product: any;
            if (item.isChannelItem) {
              const ch = channelLevelUps.find((p) => `ch_${p.item_id}` === id);
              if (ch) {
                product = {
                  id,
                  name: ch.name || `${ch.diamonds} Diamonds`,
                  priceLkr: ch.cost_gs,
                  imageUrl:
                    ch.image_url ||
                    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23888888"><path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>',
                };
              }
            } else {
              product = products.find((p) => p.id === id);
            }
            if (!product) return null;

            const outOfStock =
              !item.isChannelItem &&
              stockAvailability[id] &&
              !stockAvailability[id].isAvailable;

            const itemFeasibility = !item.isChannelItem
              ? feasibility[id]
              : undefined;

            const imgSrc = product.imageUrl?.startsWith("http")
              ? product.imageUrl
              : product.imageUrl
                ? `${process.env.NEXT_PUBLIC_API_URL?.replace(/\/api$/, "") || "http://localhost:3000"}${product.imageUrl}`
                : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23888888"><path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>';

            return (
              <React.Fragment key={id}>
                {/* Cart Item Card */}
                <div
                  className={`self-stretch p-2 bg-[#f8f8f8] rounded-[3px] inline-flex justify-center items-end gap-5 ${outOfStock ? "opacity-50" : ""}`}
                >
                  {/* Image */}
                  <div className="flex-1 h-[132px] inline-flex flex-col justify-start items-center gap-2">
                    <Image
                      width={132}
                      height={132}
                      className="self-stretch flex-1 rounded-[3px] object-contain"
                      src={imgSrc}
                      alt={product.name}
                      unoptimized
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 h-[132px] inline-flex flex-col justify-between items-start">
                    {/* Name + Remove */}
                    <div className="self-stretch inline-flex justify-between items-center">
                      <div
                        className="text-[#5998d8] text-[10px] font-medium uppercase leading-3 line-clamp-1"
                        style={{ fontFamily: geist }}
                      >
                        {product.name}
                      </div>
                      <button
                        className="ml-2 p-0.5 hover:opacity-60 transition-opacity shrink-0"
                        aria-label="Remove item"
                        onClick={() => onRemove?.(id)}
                      >
                        <svg
                          width="7"
                          height="7"
                          viewBox="0 0 7 7"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6.3457 0.192322L0.191779 6.34625"
                            stroke="#083B70"
                            strokeWidth="0.544"
                          />
                          <path
                            d="M6.3457 6.34622L0.19178 0.192298"
                            stroke="#083B70"
                            strokeWidth="0.544"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Price + Description */}
                    <div className="flex flex-col justify-start items-start gap-3">
                      <div
                        className="text-[#246fbc] text-xl font-medium uppercase leading-6"
                        style={{ fontFamily: geist }}
                      >
                        {item.isChannelItem
                          ? `${(product.priceLkr * item.qty).toLocaleString()} GS`
                          : `RS.${(getWebsitePrice(product, activeOffers) * item.qty).toLocaleString()}.00`}
                      </div>
                      <div
                        className="text-[#5998d8] text-[10px] font-medium uppercase leading-3"
                        style={{ fontFamily: geist }}
                      >
                        {item.qty} x {product.name}
                      </div>
                    </div>

                    {/* Qty Controls */}
                    <div className="w-[102px] h-7 rounded-sm inline-flex justify-between items-center">
                      {/* Minus */}
                      <button
                        className="hover:opacity-70 transition-opacity"
                        aria-label="Decrease quantity"
                        onClick={() => {
                          if (item.qty <= 1) {
                            onRemove?.(id);
                          } else {
                            onUpdateQty?.(id, item.qty - 1);
                          }
                        }}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="0.179"
                            y="-0.179"
                            width="19.643"
                            height="19.643"
                            rx="9.821"
                            transform="matrix(1 0 0 -1 0 19.643)"
                            stroke="#CFCFCF"
                            strokeWidth="0.357"
                          />
                          <path
                            d="M12.857 10H7.143"
                            stroke="#083B70"
                            strokeWidth="1.071"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>

                      {/* Qty */}
                      <div
                        className="w-16 text-center text-black text-xl font-semibold uppercase leading-6"
                        style={{ fontFamily: geist }}
                      >
                        {item.qty}
                      </div>

                      {/* Plus */}
                      <button
                        className="hover:opacity-70 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Increase quantity"
                        disabled={
                          itemFeasibility ? !itemFeasibility.canAdd : false
                        }
                        onClick={() => {
                          if (itemFeasibility && !itemFeasibility.canAdd)
                            return;
                          onUpdateQty?.(id, item.qty + 1);
                        }}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="0.179"
                            y="-0.179"
                            width="19.643"
                            height="19.643"
                            rx="9.821"
                            transform="matrix(1 0 0 -1 0 19.643)"
                            stroke="#CFCFCF"
                            strokeWidth="0.357"
                          />
                          <path
                            d="M12.857 10H7.143"
                            stroke="#083B70"
                            strokeWidth="1.071"
                            strokeLinecap="round"
                          />
                          <path
                            d="M10 12.857L10 7.143"
                            stroke="#083B70"
                            strokeWidth="1.071"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Stock warnings */}
                    {outOfStock && (
                      <span className="text-[8px] font-bold text-rose-500 uppercase mt-1">
                        Out of stock
                      </span>
                    )}
                  </div>
                </div>

                {/* Divider */}
                {index < cartEntries.length - 1 && (
                  <svg
                    width="100%"
                    height="1"
                    viewBox="0 0 327 1"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="self-stretch"
                  >
                    <path d="M0 0.25H327" stroke="#C3C3C3" strokeWidth="0.5" />
                  </svg>
                )}
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Bottom: Subtotal, Total, Checkout */}
      {!isEmpty && (
        <div className="mt-auto pt-4">
          {/* Subtotal */}
          <div className="flex items-center justify-between py-2 border-t border-[#e5e5e5]">
            <span
              className="text-[10px] font-medium text-[#888] uppercase leading-3"
              style={{ fontFamily: geist }}
            >
              Subtotal
            </span>
            <span
              className="text-sm font-semibold text-[#246fbc] uppercase"
              style={{ fontFamily: geist }}
            >
              RS.{subtotal.toLocaleString()}.00
            </span>
          </div>

          {/* Processing Charge */}
          {charges?.processingChargeEnabled &&
            charges.processingChargePercent > 0 && (
              <div className="flex items-center justify-between py-1">
                <span
                  className="text-[10px] font-medium text-[#888] uppercase leading-3"
                  style={{ fontFamily: geist }}
                >
                  Processing Charge ({charges.processingChargePercent}%)
                </span>
                <span
                  className="text-sm font-semibold text-[#246fbc] uppercase"
                  style={{ fontFamily: geist }}
                >
                  RS.
                  {Math.ceil(
                    ((subtotal - referralDiscount) *
                      charges.processingChargePercent) /
                      100,
                  ).toLocaleString()}
                  .00
                </span>
              </div>
            )}

          {/* Referral discount */}
          {isAuthenticated && useReferralBalance && referralDiscount > 0 && (
            <div className="flex items-center justify-between py-1">
              <span
                className="text-[10px] font-medium text-emerald-600 uppercase leading-3"
                style={{ fontFamily: geist }}
              >
                Referral Discount
              </span>
              <span
                className="text-sm font-semibold text-emerald-600 uppercase"
                style={{ fontFamily: geist }}
              >
                -RS.{referralDiscount.toLocaleString()}.00
              </span>
            </div>
          )}

          {/* Total */}
          <div className="flex items-center justify-between py-2 border-t border-[#e5e5e5]">
            <span
              className="text-xs font-semibold text-[#111] uppercase leading-4"
              style={{ fontFamily: geist }}
            >
              Total
            </span>
            <div className="text-right">
              <span
                className="text-xl font-bold text-[#111] uppercase"
                style={{ fontFamily: geist }}
              >
                RS.
                {total.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          {/* Referral toggle */}
          {isAuthenticated && referralBalance > 0 && subtotal > 0 && (
            <label className="flex items-center justify-between py-2 cursor-pointer">
              <span
                className="text-[10px] font-medium text-emerald-600 uppercase"
                style={{ fontFamily: geist }}
              >
                Use Referral (Rs.{referralBalance.toLocaleString()})
              </span>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={useReferralBalance}
                  onChange={(e) => setUseReferralBalance(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500" />
              </div>
            </label>
          )}

          {/* Warnings */}
          {hasOutOfStockItems && (
            <p className="text-[9px] text-center text-rose-500 font-bold uppercase tracking-wider mt-2">
              Out of stock: {outOfStockNames.join(", ")}
            </p>
          )}
          {!validationResult && (
            <p className="text-[9px] text-center text-rose-500 font-bold uppercase tracking-wider mt-2">
              Validate Player ID to unlock checkout
            </p>
          )}
          {!isAuthenticated && validationResult && (
            <p className="text-[9px] text-center text-amber-500 font-bold uppercase tracking-wider mt-2">
              Login to place orders
            </p>
          )}
          {isAuthenticated && hasMobile === false && (
            <p className="text-[9px] text-center text-rose-500 font-bold uppercase tracking-wider mt-2">
              Add mobile number to checkout
            </p>
          )}
          {!agreedToTerms &&
            validationResult &&
            isAuthenticated &&
            !hasOutOfStockItems && (
              <p className="text-[9px] text-center text-amber-500 font-bold uppercase tracking-wider mt-2">
                Agree to terms to checkout
              </p>
            )}

          {/* Terms Checkbox */}
          {isAuthenticated && !isEmpty && (
            <label className="flex items-center justify-center gap-2 py-2 cursor-pointer mt-2 text-center">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 w-3.5 h-3.5 rounded border-gray-300 text-[#246fbc] focus:ring-[#246fbc] cursor-pointer"
              />
              <span
                className="text-[8px] text-[#666] leading-3"
                style={{ fontFamily: geist }}
              >
                I agree to the{" "}
                <Link
                  href="/terms"
                  target="_blank"
                  className="text-[#246fbc] underline hover:text-[#1a4a8a]"
                >
                  Terms & Conditions
                </Link>
              </span>
            </label>
          )}

          {/* Checkout Button */}
          <button
            className="self-stretch w-full h-12 mt-3 bg-[#181818] rounded-sm inline-flex justify-center items-center gap-2.5 border-none cursor-pointer transition-all hover:bg-[#333] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={onCheckout}
            disabled={isCheckoutDisabled}
          >
            <span
              className="flex-1 text-center text-[#f9f9f9] text-[13px] font-semibold uppercase leading-4 tracking-wider"
              style={{ fontFamily: geist }}
            >
              {isSubmittingOrder ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Processing...
                </span>
              ) : !isAuthenticated ? (
                "Login to Checkout"
              ) : (
                "Checkout"
              )}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
